import { mergeClasses } from "@fluentui/react-components";
import { istObjekt, neueListenId, textOderNichts, useListenStyles } from "./listenStil";
import { asArray } from "./structuredAnswers";
import { ziffernFilter } from "./GrossZahlEingabe";

/**
 * Durchgeführte Sanierungen — je Sanierung Jahr, Umfang und (nur bei
 * „teilweise") was saniert wurde. Fachabstimmung 31.08.2026: *„vor fünf
 * Jahren …, vor acht Jahren …"* — eine einzige Sanierung reichte nicht.
 *
 * Die Liste liegt unter **einem** Answer-Key `sanierungen` (wie `schaeden`).
 * Bis 10/2026 gab es nur die Einzelfelder `sanierungUmfang`, `sanierungWas`
 * und `sanierungWann`; `sanierungenAusAntworten` macht daraus beim Öffnen den
 * ersten Eintrag, damit Bestandsdaten sichtbar und bearbeitbar bleiben.
 */

export type SanierungEintrag = {
  id: string;
  /** Jahr der Sanierung, vier Ziffern. Bestand: Freitext aus `sanierungWann`. */
  jahr?: string;
  /** `voll` | `teilweise` — dieselben Werte wie das frühere `sanierungUmfang`. */
  umfang?: string;
  /** Nur bei `umfang === "teilweise"`: was wurde saniert? */
  was?: string;
};

export const SANIERUNG_UMFANG_OPTIONEN = [
  { value: "voll", label: "vollständig" },
  { value: "teilweise", label: "teilweise" },
] as const;

/** Die früheren Einzelschlüssel — beim Bearbeiten der Liste werden sie geleert. */
export const SANIERUNG_ALT_SCHLUESSEL = ["sanierungUmfang", "sanierungWas", "sanierungWann"] as const;

export const asSanierungListe = (value: unknown): SanierungEintrag[] =>
  asArray<unknown>(value)
    .filter(istObjekt)
    .map((e, idx) => {
      const eintrag: SanierungEintrag = { id: textOderNichts(e.id) ?? `san-${idx}` };
      const jahr = textOderNichts(e.jahr);
      const umfang = textOderNichts(e.umfang);
      const was = textOderNichts(e.was);
      if (jahr) eintrag.jahr = jahr;
      if (umfang) eintrag.umfang = umfang;
      if (was) eintrag.was = was;
      return eintrag;
    });

/**
 * Liste aus den Antworten eines Gebäudes: die neue Liste, sonst — falls nur
 * die früheren Einzelfelder belegt sind — ein Eintrag daraus.
 */
export const sanierungenAusAntworten = (a: Record<string, unknown>): SanierungEintrag[] => {
  const liste = asSanierungListe(a.sanierungen);
  if (liste.length > 0) return liste;
  const umfang = textOderNichts(a.sanierungUmfang);
  const was = textOderNichts(a.sanierungWas);
  const jahr = textOderNichts(a.sanierungWann);
  if (!umfang && !was && !jahr) return [];
  const alt: SanierungEintrag = { id: "san-alt" };
  if (jahr) alt.jahr = jahr;
  if (umfang) alt.umfang = umfang;
  if (was) alt.was = was;
  return [alt];
};

type Props = {
  entries: SanierungEintrag[];
  onChange: (next: SanierungEintrag[]) => void;
};

const SanierungListe = ({ entries, onChange }: Props) => {
  const styles = useListenStyles();
  const update = (id: string, patch: Partial<SanierungEintrag>) =>
    onChange(
      entries.map((e) => {
        if (e.id !== id) return e;
        const naechste = { ...e, ...patch };
        // „Was?" gibt es nur bei „teilweise" — bei „vollständig" ist alles
        // gemeint. Geleert wird nur beim Umschalten, nicht bei jeder Eingabe:
        // ein Bestandstext neben „voll" bleibt sichtbar, bis jemand umschaltet.
        if ("umfang" in patch && naechste.umfang !== "teilweise") delete naechste.was;
        return naechste;
      }),
    );
  const remove = (id: string) => onChange(entries.filter((e) => e.id !== id));
  const add = () => onChange([...entries, { id: neueListenId("san") }]);

  return (
    <div className={styles.root}>
      {entries.length === 0 && <span className={styles.empty}>Noch keine Sanierung erfasst.</span>}
      {entries.map((entry, idx) => (
        <div key={entry.id} className={styles.card}>
          <div className={styles.head}>
            <span className={styles.badge}>{idx + 1}</span>
            <span className={styles.title}>
              {entry.jahr ? `Sanierung ${entry.jahr}` : `Sanierung ${idx + 1}`}
            </span>
            <button
              type="button"
              className={styles.removeBtn}
              onClick={() => remove(entry.id)}
              aria-label="Sanierung entfernen"
            >
              ×
            </button>
          </div>
          <div className={styles.grid}>
            <div className={styles.field}>
              <span className={styles.label}>Jahr</span>
              <input
                className={styles.input}
                name={`sanierungen-${idx}-jahr`}
                aria-label={`Sanierung ${idx + 1} — Jahr`}
                inputMode="numeric"
                placeholder="JJJJ"
                value={entry.jahr ?? ""}
                onChange={(e) => update(entry.id, { jahr: ziffernFilter(e.target.value, { maxLen: 4 }) })}
              />
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Umfang</span>
              <select
                className={styles.input}
                name={`sanierungen-${idx}-umfang`}
                aria-label={`Sanierung ${idx + 1} — Umfang`}
                value={entry.umfang ?? ""}
                onChange={(e) => update(entry.id, { umfang: e.target.value })}
              >
                <option value="">Bitte wählen</option>
                {SANIERUNG_UMFANG_OPTIONEN.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            {(entry.umfang === "teilweise" || entry.was) && (
              <div className={mergeClasses(styles.field, styles.fullSpan)}>
                <span className={styles.label}>Was wurde saniert?</span>
                <input
                  className={styles.input}
                  name={`sanierungen-${idx}-was`}
                  aria-label={`Sanierung ${idx + 1} — was wurde saniert?`}
                  placeholder="z. B. Dach, Fenster, Heizung, Leitungen"
                  value={entry.was ?? ""}
                  onChange={(e) => update(entry.id, { was: e.target.value })}
                />
              </div>
            )}
          </div>
        </div>
      ))}
      <button type="button" className={styles.addBtn} onClick={add}>
        + Sanierung hinzufügen
      </button>
    </div>
  );
};

export default SanierungListe;
